/**
 * ImageModal Component
 *
 * Handles the image display within the modal including:
 * - Full-resolution first loading with thumbnail fallback
 * - Image rendering with smooth transitions
 * - Action buttons (favorite/delete)
 * - Asset URL handling
 * - Fallback mechanisms for failed high-resolution loading
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { DeleteButton } from '../AssetActions'
import { postRequestSignedUrls } from '../../../assets/requests'
import { toast } from 'react-toastify'
import filledHeart from '../../../assets/images/full-heart.svg'
import emptyHeart from '../../../assets/images/empty-heart.svg'
import { useFavoriteToggle } from '../hooks/useFavoriteToggle'

const ImageModal = ({ state, dispatch, props, onClose }) => {
  const [currentImageSrc, setCurrentImageSrc] = useState('')
  const [, setIsLoadingHighRes] = useState(false)
  const [highResLoadFailed, setHighResLoadFailed] = useState(false)

  const currentAssetId = state.currentAsset || state.modalData?.assetId
  const targetPublishedAssets = useMemo(
    () => props?.state?.publishedAssets || state.publishedAssets,
    [props?.state?.publishedAssets, state.publishedAssets]
  )
  const targetDispatch = props?.dispatch || dispatch

  const { isFavorited, toggleFavorite: handleToggleFavorite } = useFavoriteToggle({
    assetId: currentAssetId,
    publishedAssets: targetPublishedAssets,
    assetsArray: null,
    dispatchUpdate: targetDispatch
  })

  const retryAttempts = useRef(0)
  const maxRetryAttempts = 2
  const toastShownForAsset = useRef(null)

  // Extract both thumbnail and full-resolution URLs from state
  const getImageUrls = useCallback(() => {
    if (state.modalData?.asset) {
      const { fileUrl = '', thumbnailUrl = '' } = state.modalData.asset
      return {
        thumbnail: thumbnailUrl,
        fullResolution: fileUrl,
        assetId: state.modalData.asset.id
      }
    } else {
      const currentAssetId = state.currentAsset || state.modalData?.assetId
      const asset = state.assets.find(a => a.id === currentAssetId)

      if (asset) {
        return {
          thumbnail: asset.thumbnailUrl || '',
          fullResolution: asset.fileUrl || asset.thumbnailUrl || '',
          assetId: asset.id
        }
      }
    }

    return { thumbnail: '', fullResolution: '', assetId: null }
  }, [state.modalData, state.currentAsset, state.assets])

  // Refresh signed URL for failed high-resolution image
  const refreshSignedUrl = useCallback(async (assetId, originalUrl) => {
    const userId = localStorage.getItem('username')
    if (!userId || !originalUrl) return null

    // Don't try to refresh if URL is already signed
    if (
      originalUrl.startsWith('https://') &&
      originalUrl.includes('X-Amz-Algorithm')
    ) {
      console.log('URL is already signed, skipping refresh')
      return originalUrl
    }

    try {
      console.log('Attempting to refresh signed URL for asset:', assetId)
      const signedUrls = await postRequestSignedUrls({
        userId,
        payload: [originalUrl]
      })

      if (signedUrls && signedUrls[0]) {
        console.log('Successfully refreshed signed URL')
        return signedUrls[0]
      }
    } catch (error) {
      console.error('Failed to refresh signed URL:', error)
    }

    return null
  }, [])

  // Full-resolution first loading with thumbnail fallback
  useEffect(() => {
    const { thumbnail, fullResolution, assetId } = getImageUrls()

    // Reset state for new asset
    setHighResLoadFailed(false)
    setIsLoadingHighRes(false)
    retryAttempts.current = 0
    toastShownForAsset.current = null

    // If we have a full-resolution URL, try to load it first
    if (fullResolution) {
      setIsLoadingHighRes(true)

      const highResImage = new Image()

      highResImage.onload = () => {
        setCurrentImageSrc(fullResolution)
        setIsLoadingHighRes(false)
        setHighResLoadFailed(false)
        console.log('High-resolution image loaded successfully')
      }

      highResImage.onerror = async () => {
        console.warn(
          'Failed to load full-resolution image, attempting fallback...'
        )

        // Try to refresh signed URL if we haven't exceeded retry attempts
        if (retryAttempts.current < maxRetryAttempts) {
          retryAttempts.current++
          console.log(
            `Retry attempt ${retryAttempts.current} of ${maxRetryAttempts}`
          )

          const refreshedUrl = await refreshSignedUrl(assetId, fullResolution)
          if (refreshedUrl && refreshedUrl !== fullResolution) {
            console.log('Retrying with refreshed signed URL')
            const retryImage = new Image()

            retryImage.onload = () => {
              setCurrentImageSrc(refreshedUrl)
              setIsLoadingHighRes(false)
              setHighResLoadFailed(false)
              console.log(
                'High-resolution image loaded successfully with refreshed URL'
              )
            }

            retryImage.onerror = () => {
              console.error(
                'Failed to load high-resolution image even with refreshed URL'
              )
              if (thumbnail) {
                setCurrentImageSrc(thumbnail)
                setHighResLoadFailed(true)
                console.error('Falling back to thumbnail')
                // Only show toast once per asset
                if (toastShownForAsset.current !== assetId) {
                  toastShownForAsset.current = assetId
                  toast.warning(
                    'High-res image not currently available, preview quality only. Please try again later.',
                    {
                      autoClose: 4000,
                      hideProgressBar: true
                    }
                  )
                }
              }
              setIsLoadingHighRes(false)
            }

            retryImage.src = refreshedUrl
            return
          }
        }
        console.error('All retry attempts failed, falling back to thumbnail')
        if (thumbnail) {
          setCurrentImageSrc(thumbnail)
          setHighResLoadFailed(true)
          console.log('Using thumbnail as fallback')
          // Only show toast once per asset
          if (toastShownForAsset.current !== assetId) {
            toastShownForAsset.current = assetId
            toast.warning(
              'High-res image not currently available, preview quality only. Please try again later.',
              {
                autoClose: 4000,
                hideProgressBar: true
              }
            )
          }
        }
        setIsLoadingHighRes(false)
      }

      highResImage.src = fullResolution
    } else if (thumbnail) {
      // No full-resolution URL available, use thumbnail directly
      setCurrentImageSrc(thumbnail)
      setHighResLoadFailed(true) // Mark as fallback since we're using thumbnail
      console.log('No full-resolution URL available, using thumbnail')
      // Only show toast once per asset
      if (toastShownForAsset.current !== assetId) {
        toastShownForAsset.current = assetId
        toast.info('Only preview quality available for this image.', {
          autoClose: 3000,
          hideProgressBar: true
        })
      }
    }
  }, [getImageUrls, refreshSignedUrl])

  return (
    <div className="modal-title-image">
      <div className="modal-body">
        <div className="modal-main-window">
          <div
            className="modal-image-container"
            style={{ position: 'relative' }}
          >
            <img
              className="modal-main-image fluid-image"
              alt=""
              src={currentImageSrc}
              style={{
                transition: 'opacity 0.3s ease-in-out',
                opacity: currentImageSrc ? 1 : 0.7
              }}
            />
            {/* Fallback indicator when high-resolution failed */}
            {highResLoadFailed && (
              <div
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(255,165,0,0.8)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  zIndex: 1000
                }}
              >
                Preview quality
              </div>
            )}
          </div>

          <div className="modal-icon-row">
            <div className="modal-icon-container">
              <div className="left-side-modal-buttons">
                <div className="modal-button-container">
                  <button
                    onClick={handleToggleFavorite}
                    className="button-med favorite-button"
                    aria-label={isFavorited ? 'Unfavorite' : 'Make favorite'}
                  >
                    <img
                      src={isFavorited ? filledHeart : emptyHeart}
                      alt={isFavorited ? 'Unfavorite' : 'Make favorite'}
                      className="fav-img-btn"
                    />
                  </button>
                  <DeleteButton
                    state={state}
                    props={props}
                    onClose={onClose}
                    autoHide={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageModal
